// ServiceWorkerChores.jsx
import React from 'react';
import PropTypes from 'prop-types';
import env from '../resource/env.js';
import * as MessageTypes from '../serviceWorker/AppMessageTypes.js';
import {
  serviceWorkerFallbackToMainThread,
  sendMessageToSW,
} from '../serviceWorker/helpers.js';
import { serviceWorkerLog as serviceWorkerDebug } from '../resource/debug.js';

const isProd = 'production' === env.NODE_ENV;
const SERVICEWORKER_PING_PONG_INTERVAL = 5000; // TODO: remote config
const SERVICE_WORKER_TIMEOUT = 15 * 1000; // TODO: remote config

const serviceWorkerDebugLog = serviceWorkerDebug.extend('ServiceWorkerChore');

export class ServiceWorkerChores extends React.PureComponent {
  intervalHandle = null;
  errorCount = 0;

  componentDidMount() {
    const { isPusherOnServiceWorker } = this.props;
    if (isPusherOnServiceWorker) {
      this.initialize();
    }
    sendMessageToSW({
      type: MessageTypes.UPDATE_SENTRY_CONFIG,
      payload: {
        sentryConfig: {
          environment: isProd ? 'production-client' : 'develop-client',
          level: env.SENTRY_LOG_LEVEL || 'error',
        },
      },
    });
  }

  componentDidUpdate(prevProps) {
    const { isPusherOnServiceWorker } = this.props;
    if (
      isPusherOnServiceWorker !== prevProps.isPusherOnServiceWorker &&
      isPusherOnServiceWorker
    ) {
      this.initialize();
    }
  }

  componentWillUnmount() {
    clearInterval(this.intervalHandle);
    this.detachListeners();
  }

  initialize() {
    if (isProd) {
      this.startPingingServiceWorker();
      this.attachListeners();
    }
  }

  startPingingServiceWorker = () => {
    if (this.intervalHandle) clearInterval(this.intervalHandle);

    this.intervalHandle = setInterval(() => {
      const {
        serviceWorkerStatus,
        updateServiceWorkStatus,
        isPusherOnServiceWorker,
      } = this.props;

      const { ping, pong, error } = serviceWorkerStatus;

      if (!isPusherOnServiceWorker) {
        serviceWorkerDebugLog('service worker is not using');

        this.resetServiceWorkerState(Date.now());
        this.stopPingServiceWorker();
        this.detachListeners();
        this.errorCount = 0;

        return;
      }

      serviceWorkerDebugLog('serviceWorkerStatus:', { ...serviceWorkerStatus });

      if (ping - pong > SERVICE_WORKER_TIMEOUT || this.errorCount >= 2) {
        // there is no pong response from service worker
        serviceWorkerDebugLog(
          'service worker gone',
          serviceWorkerStatus,
          this.errorCount
        );
        this.resetServiceWorkerState(Date.now());
        this.stopPingServiceWorker();
        this.detachListeners();
        this.errorCount = 0;
        serviceWorkerFallbackToMainThread();
      } else if (error) {
        // if there is pusher error, and the ping pong does not update
        this.errorCount += 1;
      } else if (!error) {
        // there is no pusher error, keep ping pong
        const now = Date.now();
        updateServiceWorkStatus({
          status: {
            ping: now,
          },
        });
        sendMessageToSW({
          type: MessageTypes.PING_SERVICE_WORKER,
          payload: {
            timestamp: now,
          },
        });
        if (this.errorCount) this.errorCount = 0;
      }
    }, SERVICEWORKER_PING_PONG_INTERVAL);
  };

  restartPingServiceWorker = () => {
    serviceWorkerDebugLog('restart ping service worker');
    const now = Date.now();
    this.resetServiceWorkerState(now);
    setTimeout(() => {
      // wait for 2 secs for pusher might reconnect
      serviceWorkerDebugLog('after 2 sec to ping service worker');
      sendMessageToSW({
        type: MessageTypes.PING_SERVICE_WORKER,
        payload: {
          timestamp: now,
        },
      });
    }, 2000); // TODO: remote config
    this.startPingingServiceWorker();
  };

  stopPingServiceWorker = () => {
    serviceWorkerDebugLog('stop ping service worker');
    if (this.intervalHandle) {
      clearInterval(this.intervalHandle);
      this.intervalHandle = null;
    }
  };

  resetServiceWorkerState = now => {
    const { updateServiceWorkStatus } = this.props;

    updateServiceWorkStatus({
      status: {
        ping: now,
        pong: now,
        error: '',
      },
    });
  };

  attachListeners = () => {
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    window.addEventListener('pageshow', this.pageshowHandler);
    window.addEventListener('pagehide', this.pagehideHandler);
  };

  detachListeners = () => {
    document.removeEventListener(
      'visibilitychange',
      this.visibilityChangeHandler
    );
    window.removeEventListener('pageshow', this.pageshowHandler);
    window.removeEventListener('pagehide', this.pagehideHandler);
  };

  visibilityChangeHandler = () => {
    serviceWorkerDebugLog('visibilityState:', document.visibilityState);
    if (document.visibilityState === 'visible' && !this.intervalHandle)
      this.restartPingServiceWorker();
    else this.stopPingServiceWorker();
  };

  pageshowHandler = () => {
    serviceWorkerDebugLog('pageshow');
    if (!document.hidden && !this.intervalHandle)
      this.restartPingServiceWorker();
  };

  pagehideHandler = () => {
    serviceWorkerDebugLog('pagehide');
    if (document.hidden) this.stopPingServiceWorker();
  };

  render() {
    return null;
  }
}

ServiceWorkerChores.propTypes = {
  serviceWorkerStatus: PropTypes.object,
  isPusherOnServiceWorker: PropTypes.bool,
  updateServiceWorkStatus: PropTypes.func,
};

ServiceWorkerChores.defaultProps = {
  serviceWorkerStatus: {
    ping: Date.now(),
    pong: Date.now(),
    error: '',
  },
  isPusherOnServiceWorker: null,
  updateServiceWorkStatus: () => null,
};

export default ServiceWorkerChores;
